import { NextUIProvider } from '@nextui-org/react'
import { AnimatePresence } from 'framer-motion'
import { FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Route, Routes, useNavigate } from 'react-router-dom'
import BackofficeLayout from './components/Layout/NewLayout/Layout/BackofficeLayout'
import Layout from './components/Layout/Layout'
import LoadSeller from './components/LoadSeller/LoadSeller'
import { DEVICE_REQUESTS_ROLES, PrivateRoutes, PublicRoutes } from './constants/routes'
import AuthRoleRouterGuard from './guards/AuthRoleRouterGuard'
import LogisticBoxRouterGuard from './guards/LogisticBoxRouterGuard'
import SlugRouterGuard from './guards/SlugRouterGuard'
import { useVersionCheck } from './hooks/useVersionCheck'
import { IConfigSeller } from './models/configSeller'
import { hasRedesign } from '@/utils/redesignSellers'
import BulkSamsungPage from './pages/admin/BulkSamsungPage/BulkSamsungPage'
import BulkTelcelPage from './pages/admin/BulkTelcelPage/BulkTelcelPage'
import MailingPage from './pages/admin/MailingPage/MailingPage'
import DataBankPage from './pages/backoffice/DataBankPage/DataBankPage'
import HomePage from './pages/backoffice/HomePage/HomePage'
import ListPage from './pages/backoffice/ListPage/ListPage'
import LoginPage from './pages/backoffice/LoginPage/LoginPage'
import NotFoundPage from './pages/backoffice/NotFoundPage/NotFoundPage'
import PickupForm from './pages/backoffice/PickupForm/PickupForm'
import PickupFormSamsung from './pages/backoffice/PickupForm/PickupFormSamsung'
import TradeInPage from './pages/backoffice/TradeInPage/TradeInPage'
import DashboardPage from './pages/backoffice/DashboardPage/DashboardPage'
import UploadPrices from './pages/backoffice/UploadPrices/UploadPrices'
import BankAccountPage from './pages/cliente/BankAccountPage/BankAccountPage'
import ContractPage from './pages/cliente/ContractPage/ContractPage'
import ChecklistPage from './pages/cliente/ChecklistPage/ChecklistPage'
import { HomePageProvider } from './pages/cliente/FormularioClientePage/context/FormularioClienteContext'
import FormularioClientePage from './pages/cliente/FormularioClientePage/FormularioClientePage'
import SuccessPage from './pages/cliente/SuccessPage/SuccessPage'
import './theme/App.css'
import VersionCheckPage from './pages/backoffice/VersionCheck/VersionCheckPage'
import ContributionsPage from './pages/admin/ContributionsPage/ContributionsPage'
import ContributionFormPage from './pages/admin/ContributionFormPage/ContributionFormPage'
import { FormCampaignProvider } from './pages/admin/ContributionFormPage/context/FormCampaignContext'
import { DashboardProvider } from './pages/backoffice/DashboardPage/context/DashboardContext'
import LogisticBoxPage from './pages/backoffice/LogisticBoxPage/LogisticBoxPage'
import { LogisticBoxProvider } from './pages/backoffice/LogisticBoxPage/context/LogisticBoxContext'
import DeviceRequestsPage from './pages/backoffice/DeviceRequestsPage/DeviceRequestsPage'

const App: FC = () => {
  const navigate = useNavigate()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)

  const updateAvailable = useVersionCheck()
  const sellersNewLayoutProd = [
    'ti_samsung_mx', 'ti_macstore', 'ti_oppo', 'ti_elektra_mx', 'ti_xiaomi', 'ti_huawei_mx', 'ti_coppel', 'ti_telcel', 'ti_liverpool_mx', 'ti_telcel_corp_mx',
    'ti_falabella', 'ti_reusecl', 'ti_motorola', 'ti_wom', 'ti_claro', 'ti_paris', 'ti_ripley_cl', 'ti_huawei_cl', 'ti_huawei_online_cl', 'ti_huawei_online_mx', 'ti_huawei_online_pe', 'ti_mercado_libre_cl', 'ti_tecnobuy_cl',
    'ti_falabella_pe', 'ti_samsung_pe', 'ti_samsung_pe_rom', 'ti_conecta_peru', 'ti_phantom', 'ti_ripley_pe', 'ti_huawei_pe'
  ]
  console.log('meta')
  const sellersNewLayoutDev = [
    'ti_samsung_mx', 'ti_macstore', 'ti_oppo', 'ti_elektra_mx', 'ti_xiaomi', 'ti_huawei_mx', 'ti_coppel', 'ti_telcel', 'ti_liverpool_mx', 'ti_telcel_corp_mx',
    'ti_falabella', 'ti_reusecl', 'ti_motorola', 'ti_wom', 'ti_claro', 'ti_paris', 'ti_ripley_cl', 'ti_huawei_cl', 'ti_huawei_online_cl', 'ti_huawei_online_mx', 'ti_huawei_online_pe', 'ti_mercado_libre_cl', 'ti_tecnobuy_cl',
    'ti_falabella_pe', 'ti_samsung_pe', 'ti_samsung_pe_rom', 'ti_conecta_peru', 'ti_phantom', 'ti_ripley_pe', 'ti_huawei_pe'
  ]
  /* Header + sidebar nuevos (BackofficeLayout). Los sellers del rediseño entran siempre,
     sin tener que acordarse de sumarlos también a las listas de arriba: alcanza con
     agregarlos a REDESIGN_SELLERS (@/utils/redesignSellers). */
  const folderApp = String(configSeller.config?.s3?.FOLDER_APP)
  const usaRediseno = hasRedesign(folderApp)
  const usaLayoutNuevo = usaRediseno || (
    import.meta.env.VITE_STAGE === 'DEV'
      ? sellersNewLayoutDev.includes(folderApp)
      : sellersNewLayoutProd.includes(folderApp)
  )

  /* Marca el documento con la paleta que le toca a este seller. theme/App.css define los
     tokens de color del rediseño bajo `:root[data-redesign='true']`, y como los componentes de
     NextUI compilan clases `bg-primary`/`text-primary`, con esto quedan del color correcto
     tanto ellos como los componentes nuevos, sin duplicar nada. */
  useEffect(() => {
    if (usaRediseno) document.documentElement.setAttribute('data-redesign', 'true')
    else document.documentElement.removeAttribute('data-redesign')
  }, [usaRediseno])

  const rolesDashboard = (import.meta.env.VITE_COUNTRY === 'MX') ? [17, 19, 27] : (import.meta.env.VITE_COUNTRY === 'CL') ? [1, 33, 40] : (import.meta.env.VITE_COUNTRY === 'PE') ? [1, 13, 18] : []
  if (updateAvailable) {
    console.log('🚀 ~ updateAvailable:', updateAvailable)
    return <VersionCheckPage />
  }

  return (
    <NextUIProvider navigate={navigate} locale='es-ES'>
      <AnimatePresence mode='wait'>
        <Routes>
          <Route path={PublicRoutes.NOTFOUND} element={<NotFoundPage />} />
          <Route path={PublicRoutes.ROOT} element={<LoadSeller />}>
            <Route path={PublicRoutes.CONTRACT} element={<ContractPage />} />
            <Route path={PublicRoutes.CHECKLIST} element={<ChecklistPage />} />
            <Route path={PublicRoutes.ROOT} element={<h1>Es necesario ingresar un slug</h1>} />
            {
                  configSeller.config?.s3 &&
                  configSeller.config.s3.FOLDER_APP !== 'ti_macstore' &&
                  configSeller.config.s3.FOLDER_APP !== 'ti_coppel' &&
                  configSeller.config.s3.FOLDER_APP !== 'ti_samsung_mx'
                    ? (
                      <>
                        <Route path={PrivateRoutes.PICKUP_FORM} element={<PickupForm />} />
                      </>
                      )
                    : null
                }
            <Route element={<SlugRouterGuard sellerSlug='samsung' />}>
              <Route path={PublicRoutes.BANKACCOUNT} element={<BankAccountPage />} />
            </Route>
            <Route path={PublicRoutes.LOGIN} element={<LoginPage />} />

            <Route element={usaLayoutNuevo ? <BackofficeLayout /> : <Layout />}>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_CUSTODIA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.HOME + '*'} element={<HomePage />} />
              </Route>

              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.TRADEIN} element={<TradeInPage />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)]} />}>
                <Route
                  path={PrivateRoutes.PICKUP_FORM}
                  element={usaLayoutNuevo ? <PickupFormSamsung /> : <PickupForm />}
                />
              </Route>

              {usaLayoutNuevo && (
                <Route element={<AuthRoleRouterGuard roles={rolesDashboard} />}>
                  <Route path={PrivateRoutes.DASHBOARD} element={<DashboardProvider><DashboardPage /></DashboardProvider>} />
                </Route>
              )}

              {/* Dispositivos que se pidió incorporar al cotizador: solo admin y jefe de tienda. */}
              <Route element={<AuthRoleRouterGuard roles={DEVICE_REQUESTS_ROLES} />}>
                <Route path={PrivateRoutes.DEVICE_REQUESTS} element={<DeviceRequestsPage />} />
              </Route>

              {/* Acceso: abierto a todos en slugs liberados (Falabella, Ripley); en el resto, rol permitido Y correo en la lista de liberación gradual. */}
              <Route element={<LogisticBoxRouterGuard />}>
                <Route path={PrivateRoutes.LOGISTIC_BOX} element={<LogisticBoxProvider><LogisticBoxPage /></LogisticBoxProvider>} />
              </Route>

              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)]} />}>
                <Route element={<SlugRouterGuard sellerSlug='falabella' />}>
                  <Route path={PrivateRoutes.MAILING} element={<MailingPage />} />
                </Route>
                <Route element={<SlugRouterGuard sellerSlug='samsung' />}>
                  <Route path={PrivateRoutes.LOADSAMSUNGDATA} element={<BulkSamsungPage />} />
                </Route>
                <Route element={<SlugRouterGuard folderApp='ti_telcel_corp_mx' />}>
                  <Route path={PrivateRoutes.LOADTELCELDATA} element={<BulkTelcelPage />} />
                </Route>
                <Route element={<SlugRouterGuard sellerSlug={['samsung', 'huawei-online', 'samsung-rom']} />}>
                  <Route path={PrivateRoutes.DATABANK} element={<DataBankPage />} />
                </Route>
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION)]} />}>
                <Route path={PrivateRoutes.CONTRIBUTIONS} element={<ContributionsPage />} />
                <Route path={PrivateRoutes.CONTRIBUTION} element={<FormCampaignProvider><ContributionFormPage /></FormCampaignProvider>} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION)]} />}>
                <Route path={PrivateRoutes.SUBIR_PRECIOS} element={<UploadPrices />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_TECHNICIANS)]} />}>
                <Route path={PrivateRoutes.LISTADO_TECNICOS} element={<ListPage />} />
              </Route>
              <Route element={<AuthRoleRouterGuard roles={[Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_CUSTODIA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS), Number(import.meta.env.VITE_PERFIL_COURIER), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)]} />}>
                <Route path={PrivateRoutes.FORMULARIO} element={<HomePageProvider><FormularioClientePage /></HomePageProvider>} />
                <Route path={PrivateRoutes.SUCCESS} element={<SuccessPage />} />
              </Route>
            </Route>
            <Route path={PublicRoutes.SLUG} element={<HomePageProvider><FormularioClientePage /></HomePageProvider>} />
            <Route path={PublicRoutes.SUCCESS} element={<SuccessPage />} />
            <Route path={PrivateRoutes.HOME + '*'} element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </NextUIProvider>
  )
}

export default App
